import { useState } from 'react';
import {
    Button,
    IconButton,
    Tooltip,
    Popover,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Divider
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useDateStore } from '../stores/useDateStore';
import { useOperationalMapStore } from '../stores/useOperationalMapStore';
import { useLayoutStore } from '../hooks/screen/useLayoutStore';
import { m } from '../utils/dateUtils';
import { usePermissions } from '../hooks/auth/usePermissions';
import { useLoadingStore, selectIsMainLoading } from '../stores/useLoadingStore';

export default function DateSelector() {

    const isMainLoading = useLoadingStore(selectIsMainLoading);

    const selectedDate = useDateStore(
        state => state.selectedDate
    );

    const setSelectedDate = useDateStore(
        state => state.setSelectedDate
    );

    const currentDayLabel = useDateStore(
        state => state.currentDayLabel
    );

    const isToday = useDateStore(
        state => state.isToday
    );

    const setOperationalMapDateRange = useOperationalMapStore(
        state => state.setOperationalMapDateRange
    );

    const isOperationalMapMode = useOperationalMapStore(
        state => state.isOperationalMapMode
    );

    const isCompactMode = useLayoutStore(
        state => state.isCompactMode
    );

    const selectorMode =
        isOperationalMapMode
            ? 'range'
            : 'day';

    const [anchorEl, setAnchorEl] = useState(null);
    const [rangeStartDate, setRangeStartDate] = useState(() => m());
    const [rangeEndDate, setRangeEndDate] = useState(() => m());

    const isRangeToday =
        rangeStartDate?.isSame(m(), 'day') &&
        rangeEndDate?.isSame(m(), 'day');

    const {
        canPickyDate,
        canUseOperationalMap,
        dateRange,
        minAllowedDate
    } = usePermissions();

    // =========================
    // LIMITES DE DATA
    // =========================

    // Limite global da Torre
    // (minDate do backend ou no máximo 12 meses atrás)
    const systemMinDate = m(minAllowedDate);

    // Limite específico do usuário para o modo Torre
    const userMinDate = m().subtract(
        dateRange.minDays,
        'day'
    );

    // No modo Torre deve prevalecer o limite mais restritivo
    const towerMinDate = systemMinDate.isAfter(
        userMinDate,
        'day'
    )
        ? systemMinDate
        : userMinDate;

    // No mapa operacional o período máximo continua sendo 1 mês
    const rangeMinDate = rangeEndDate
        ? rangeEndDate
            .clone()
            .subtract(1, 'month')
        : systemMinDate;

    // O início do range também nunca pode ultrapassar
    // o limite global da Torre
    const rangeStartMinDate = systemMinDate.isAfter(
        rangeMinDate,
        'day'
    )
        ? systemMinDate
        : rangeMinDate;

    const open = Boolean(anchorEl);

    const handleOpen = (event) => {

        if (!canPickyDate) {
            return;
        }

        if (isOperationalMapMode) {

            const {
                startDate,
                endDate
            } = useOperationalMapStore
                .getState()
                .operationalMapDateRange;

            setRangeStartDate(
                startDate.clone()
            );

            setRangeEndDate(
                endDate.clone()
            );
        }

        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {

        setAnchorEl(null);
    };

    const handleModeChange = (_, mode) => {

        if (mode === null) {
            return;
        }

        useOperationalMapStore
            .getState()
            .setOperationalMapMode(
                mode === 'range'
            );
    };

    const handleDateChange = (day) => {

        setSelectedDate(day);

        handleClose();
    };

    const handleRangeStartChange = (day) => {

        setRangeStartDate(day);

        if (!rangeEndDate) {
            return;
        }

        if (rangeEndDate.isBefore(day, 'day')) {

            setRangeEndDate(day);

            return;
        }

        const maxEndDate = day
            .clone()
            .add(1, 'month');

        if (rangeEndDate.isAfter(maxEndDate, 'day')) {
            setRangeEndDate(maxEndDate);
        }
    };

    const handleRangeEndChange = (day) => {

        setRangeEndDate(day);

        if (!rangeStartDate) {
            return;
        }

        if (rangeStartDate.isAfter(day, 'day')) {

            setRangeStartDate(day);

            return;
        }

        const minStartDate = day
            .clone()
            .subtract(1, 'month');

        if (rangeStartDate.isBefore(minStartDate, 'day')) {
            setRangeStartDate(minStartDate);
        }
    };

    const handleResetRangeToday = () => {

        const today = m();

        setRangeStartDate(
            today.clone()
        );

        setRangeEndDate(
            today.clone()
        );

        useOperationalMapStore
            .getState()
            .setOperationalMapDateRange(
                today,
                today
            );
    };

    const handleApplyRange = () => {

        setOperationalMapDateRange(
            rangeStartDate,
            rangeEndDate
        );

        handleClose();
    };

    const buttonLabel =
        selectorMode === 'day'
            ? currentDayLabel
            : 'MAPA OPERACIONAL';

    const dateColor =
        (isToday || selectorMode === 'range')
            ? 'success'
            : 'warning';

    return (
        <>
            <Tooltip
                title={
                    isCompactMode
                        ? buttonLabel
                        : canUseOperationalMap
                            ? 'Selecione uma data ou um período.'
                            : ''
                }
                arrow
            >
                <span>
                    {isCompactMode ? (
                        <IconButton
                            component={canPickyDate ? 'button' : 'div'}
                            onClick={handleOpen}
                            sx={(theme) => ({
                                width: 40,
                                height: 40,
                                backgroundColor: theme.palette[dateColor].main,
                                color: theme.palette[dateColor].contrastText,

                                cursor: canPickyDate
                                    ? 'pointer'
                                    : 'default',

                                '&:hover': {
                                    backgroundColor: theme.palette[dateColor].dark
                                }
                            })}
                            disabled={isMainLoading}
                        >
                            <CalendarMonthIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                    ) : (
                        <Button
                            component={canPickyDate ? 'button' : 'div'}
                            variant="contained"
                            color={dateColor}
                            sx={{
                                mb: 0,
                                cursor: canPickyDate
                                    ? 'pointer'
                                    : 'default'
                            }}
                            onClick={handleOpen}
                            disabled={isMainLoading}
                        >
                            {buttonLabel}
                        </Button>
                    )}
                </span>
            </Tooltip>

            {canPickyDate && dateRange && (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                >
                    <Box sx={{
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column' 
                        }}
                    >
                        
                            {canUseOperationalMap && (
                                <Box sx={{flexGrow: 1}}>
                                    <Typography
                                        variant='h6'
                                        color='warning'
                                        mb={1}
                                        textAlign={'left'}
                                    >
                                        Modo de Operação:
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={selectorMode}
                                        exclusive
                                        fullWidth
                                        size="small"
                                        onChange={handleModeChange}
                                        sx={{ mb: 1 }}
                                    >
                                        <ToggleButton value="day">
                                            TORRE
                                        </ToggleButton>

                                        <ToggleButton value="range">
                                            MAPA OPERACIONAL
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            )}
                        
                        <Box sx={{display: 'flex'}}>
                            <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                {selectorMode === 'range' && (
                                    <Typography sx={{px: 1}} textAlign='center' variant='subtitle1'>Data Início:</Typography>
                                )}
                                <DateCalendar
                                    value={
                                        selectorMode === 'range'
                                            ? rangeStartDate
                                            : selectedDate
                                    }
                                    onChange={
                                        selectorMode === 'range'
                                            ? handleRangeStartChange
                                            : handleDateChange
                                    }
                                    minDate={
                                        selectorMode === 'range'
                                            ? rangeStartMinDate
                                            : towerMinDate
                                    }
                                    maxDate={
                                        selectorMode === 'range'
                                            ? rangeEndDate
                                            : m().add(
                                                dateRange.maxDays,
                                                'day'
                                            )
                                    }
                                />
                            </Box>

                            {selectorMode === 'range' && (
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                        mx: 1
                                    }}
                                />
                            )}
                            
                            {selectorMode === 'range' && (
                                <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <Typography sx={{px: 1}} textAlign='center' variant='subtitle1'>Data Fim:</Typography>
                                    <DateCalendar
                                        value={rangeEndDate}
                                        onChange={handleRangeEndChange}
                                        minDate={rangeStartDate}
                                        maxDate={
                                            rangeStartDate
                                                ? rangeStartDate.clone().add(1, 'month')
                                                : undefined
                                        }
                                    />
                                </Box>
                            )}
                        </Box>
                        {selectorMode === 'range' && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                    px: 1,
                                    pb: 1
                                }}
                            >
                                <Button
                                    onClick={handleResetRangeToday}
                                    disabled={isRangeToday}
                                >
                                    Hoje
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={handleApplyRange}
                                >
                                    Aplicar
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Popover>
            )}
        </>
    );
}